import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import type { LoSiteFeaturesLayout } from '@/lib/lyrraone-site-theme-profiles';
import type { LoSiteElementStyle } from '@/lib/lyrraone-site-elements';
import { elementStyleClass, elementStyleToCss } from '@/lib/lyrraone-site-elements';
import { LoSiteFeatureMarker } from '@/components/lyrraone/site-themes/LoSiteFeatureMarker';
import { ThemeEditable } from '@/components/lyrraone/site-themes/LoSiteThemeEditable';
import { cn } from '@/lib/utils';

interface LoSiteThemedFeaturesProps {
  layout: LoSiteFeaturesLayout;
  block: LoSiteBlock;
  blockKey: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
}

function SectionHead({
  kicker,
  title,
  subtitle,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
}: {
  kicker?: string;
  title?: string;
  subtitle?: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
}) {
  if (!kicker && !title && !subtitle) return null;
  return (
    <div className="lo-section-head">
      {kicker && (
        <ThemeEditable
          elementKey="features.kicker"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn('lo-kicker', elementStyleClass(styleOf('features.kicker')))}
          style={elementStyleToCss(styleOf('features.kicker'))}
          as="span"
          editableText
        >
          {kicker}
        </ThemeEditable>
      )}
      {title && (
        <ThemeEditable
          elementKey="features.title"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn('lo-h2', elementStyleClass(styleOf('features.title')))}
          style={elementStyleToCss(styleOf('features.title'))}
          as="h2"
          editableText
        >
          {title}
        </ThemeEditable>
      )}
      {subtitle && (
        <ThemeEditable
          elementKey="features.subtitle"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn('lo-lead', elementStyleClass(styleOf('features.subtitle')))}
          style={elementStyleToCss(styleOf('features.subtitle'))}
          as="p"
          editableText
        >
          {subtitle}
        </ThemeEditable>
      )}
    </div>
  );
}

function FeatureItem({
  item,
  index,
  layout,
  listStyle,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
  variant,
}: {
  item: { title: string; description: string; icon?: string; price?: string; period?: string; featured?: boolean };
  index: number;
  layout: LoSiteFeaturesLayout;
  listStyle: NonNullable<LoSiteBlock['featuresListStyle']>;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
  variant?: LoSiteBlock['featuresVariant'];
}) {
  const titleKey = `features.item.${index}.title`;
  const descKey = `features.item.${index}.description`;

  if (variant === 'pain') {
    return (
      <div className="lo-pain-item">
        <span className="lo-pain-icon" aria-hidden>✕</span>
        <ThemeEditable
          elementKey={titleKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(titleKey)))}
          style={elementStyleToCss(styleOf(titleKey))}
          as="p"
          editableText
        >
          {item.title}
        </ThemeEditable>
      </div>
    );
  }

  if (variant === 'steps') {
    return (
      <article className="lo-step">
        <div className="lo-step-num">{String(index + 1).padStart(2, '0')}</div>
        <ThemeEditable
          elementKey={titleKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(titleKey)))}
          style={elementStyleToCss(styleOf(titleKey))}
          as="h3"
          editableText
        >
          {item.title}
        </ThemeEditable>
        <ThemeEditable
          elementKey={descKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(descKey)))}
          style={elementStyleToCss(styleOf(descKey))}
          as="p"
          editableText
        >
          {item.description}
        </ThemeEditable>
      </article>
    );
  }

  if (variant === 'pricing') {
    return (
      <article className={cn('lo-price-card', item.featured && 'featured')}>
        <ThemeEditable
          elementKey={titleKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(titleKey)))}
          style={elementStyleToCss(styleOf(titleKey))}
          as="h3"
          editableText
        >
          {item.title}
        </ThemeEditable>
        {item.price && <div className="price">{item.price}</div>}
        {item.period && <div className="period">{item.period}</div>}
        <ThemeEditable
          elementKey={descKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(descKey)))}
          style={elementStyleToCss(styleOf(descKey))}
          as="p"
          editableText
        >
          {item.description}
        </ThemeEditable>
      </article>
    );
  }

  if (variant === 'stats') {
    return (
      <div>
        <ThemeEditable
          elementKey={titleKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(titleKey)))}
          style={elementStyleToCss(styleOf(titleKey))}
          as="dt"
          editableText
        >
          {item.title}
        </ThemeEditable>
        <ThemeEditable
          elementKey={descKey}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn(elementStyleClass(styleOf(descKey)))}
          style={elementStyleToCss(styleOf(descKey))}
          as="dd"
          editableText
        >
          {item.description}
        </ThemeEditable>
      </div>
    );
  }

  if (layout === 'strip') {
    return (
      <article className="lo-st-features__strip-item">
        <LoSiteFeatureMarker item={item} index={index} listStyle={listStyle} variant="strip" />
        <div>
          <ThemeEditable
            elementKey={titleKey}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-st-features__item-title', elementStyleClass(styleOf(titleKey)))}
            style={elementStyleToCss(styleOf(titleKey))}
            as="h3"
            editableText
          >
            {item.title}
          </ThemeEditable>
          <ThemeEditable
            elementKey={descKey}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className={cn('lo-st-features__item-desc', elementStyleClass(styleOf(descKey)))}
            style={elementStyleToCss(styleOf(descKey))}
            as="p"
            editableText
          >
            {item.description}
          </ThemeEditable>
        </div>
      </article>
    );
  }

  return (
    <article className="lo-st-features__card">
      <LoSiteFeatureMarker item={item} index={index} listStyle={listStyle} variant="card" />
      <ThemeEditable
        elementKey={titleKey}
        editMode={editMode}
        selectedElementKey={selectedElementKey}
        onSelectElement={onSelectElement}
        onInlineTextChange={onInlineTextChange}
        className={cn('lo-st-features__item-title', elementStyleClass(styleOf(titleKey)))}
        style={elementStyleToCss(styleOf(titleKey))}
        as="h3"
        editableText
      >
        {item.title}
      </ThemeEditable>
      <ThemeEditable
        elementKey={descKey}
        editMode={editMode}
        selectedElementKey={selectedElementKey}
        onSelectElement={onSelectElement}
        onInlineTextChange={onInlineTextChange}
        className={cn('lo-st-features__item-desc', elementStyleClass(styleOf(descKey)))}
        style={elementStyleToCss(styleOf(descKey))}
        as="p"
        editableText
      >
        {item.description}
      </ThemeEditable>
    </article>
  );
}

export function LoSiteThemedFeatures({
  layout,
  block,
  blockKey,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
}: LoSiteThemedFeaturesProps) {
  const items = block.items ?? [];
  const variant = block.featuresVariant ?? 'default';
  const listStyle = block.featuresListStyle ?? 'numbered';
  const kicker = block.featuresKicker;
  const footer = block.featuresFooter;

  if (variant === 'cta-band') {
    return (
      <section key={blockKey} className="lo-cta-final">
        <div className="lo-wrap">
          <SectionHead
            kicker={kicker}
            title={block.title}
            subtitle={block.subtitle}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            styleOf={styleOf}
          />
          {(block.ctaText || editMode) && (
            <ThemeEditable
              elementKey="features.cta"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className="lo-btn lo-btn-primary lo-btn-lg"
              as="span"
              editableText
            >
              {block.ctaText ?? 'Commencer'}
            </ThemeEditable>
          )}
        </div>
      </section>
    );
  }

  if (variant === 'pain') {
    return (
      <section key={blockKey} className="lo-section lo-pain">
        <div className="lo-wrap lo-pain-grid">
          <SectionHead
            title={block.title}
            subtitle={block.subtitle}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            styleOf={styleOf}
          />
          <div className="lo-pain-items">
            {items.map((item, i) => (
              <FeatureItem
                key={i}
                item={item}
                index={i}
                layout={layout}
                listStyle={listStyle}
                variant={variant}
                editMode={editMode}
                selectedElementKey={selectedElementKey}
                onSelectElement={onSelectElement}
                onInlineTextChange={onInlineTextChange}
                styleOf={styleOf}
              />
            ))}
          </div>
          {footer && (
            <div className="lo-pain-solution">
              <span aria-hidden>→</span>
              <ThemeEditable
                elementKey="features.footer"
                editMode={editMode}
                selectedElementKey={selectedElementKey}
                onSelectElement={onSelectElement}
                onInlineTextChange={onInlineTextChange}
                as="p"
                editableText
              >
                {footer}
              </ThemeEditable>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (variant === 'steps') {
    return (
      <section key={blockKey} className="lo-section alt">
        <div className="lo-wrap">
          <SectionHead
            kicker={kicker}
            title={block.title}
            subtitle={block.subtitle}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            styleOf={styleOf}
          />
          <div className="lo-steps">
            {items.map((item, i) => (
              <FeatureItem
                key={i}
                item={item}
                index={i}
                layout={layout}
                listStyle={listStyle}
                variant={variant}
                editMode={editMode}
                selectedElementKey={selectedElementKey}
                onSelectElement={onSelectElement}
                onInlineTextChange={onInlineTextChange}
                styleOf={styleOf}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'stats') {
    return (
      <section key={blockKey} className="lo-section" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="lo-wrap">
          <dl className="lo-hero-v2-stats">
            {items.map((item, i) => (
              <FeatureItem
                key={i}
                item={item}
                index={i}
                layout={layout}
                listStyle={listStyle}
                variant={variant}
                editMode={editMode}
                selectedElementKey={selectedElementKey}
                onSelectElement={onSelectElement}
                onInlineTextChange={onInlineTextChange}
                styleOf={styleOf}
              />
            ))}
          </dl>
        </div>
      </section>
    );
  }

  if (variant === 'pricing') {
    return (
      <section key={blockKey} className="lo-section">
        <div className="lo-wrap">
          <SectionHead
            title={block.title}
            subtitle={block.subtitle}
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            styleOf={styleOf}
          />
          <div className="lo-pricing">
            {items.map((item, i) => (
              <FeatureItem
                key={i}
                item={item}
                index={i}
                layout={layout}
                listStyle={listStyle}
                variant={variant}
                editMode={editMode}
                selectedElementKey={selectedElementKey}
                onSelectElement={onSelectElement}
                onInlineTextChange={onInlineTextChange}
                styleOf={styleOf}
              />
            ))}
          </div>
          {footer && (
            <ThemeEditable
              elementKey="features.footer"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className="lo-pricing-note"
              as="p"
              editableText
            >
              {footer}
            </ThemeEditable>
          )}
        </div>
      </section>
    );
  }

  const gridClass =
    layout === 'strip'
      ? 'lo-st-features__strip'
      : layout === 'bento'
        ? 'lo-st-features__bento'
        : layout === 'minimal'
          ? 'lo-st-features__minimal'
          : 'lo-st-features__grid';

  return (
    <section key={blockKey} className={cn('lo-st-features', `lo-st-features--${layout}`)}>
      <div className="lo-wrap">
        <SectionHead
          kicker={kicker}
          title={block.title}
          subtitle={block.subtitle}
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          styleOf={styleOf}
        />
        <div className={gridClass}>
          {items.map((item, i) => (
            <FeatureItem
              key={i}
              item={item}
              index={i}
              layout={layout}
              listStyle={listStyle}
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              styleOf={styleOf}
            />
          ))}
        </div>
        {footer && (
          <ThemeEditable
            elementKey="features.footer"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
            className="lo-pricing-note mt-6 text-center text-sm text-gray-500"
            as="p"
            editableText
          >
            {footer}
          </ThemeEditable>
        )}
      </div>
    </section>
  );
}
